import { FC, useEffect, useRef, useState } from 'react';
import { TextInput, TextInputProps } from './TextInput.tsx';

export type IIntegerInputProps = Omit<TextInputProps, 'type' | 'inputmode' | 'value' | 'onChange'> & {
    max?: number;
    value?: number;
    onChange?: (value: number | undefined) => void;
};

export const IntegerInput: FC<IIntegerInputProps> = ({
    max = 9999999999,
    value,
    onChange,
    ...otherProps
}) => {

    const inputRef = useRef<HTMLIonInputElement>(null);

    // maintain internal copy of value, so we can revert invalid input
    const [internalValue, setInternalValue] = useState<number | undefined>(value);
    useEffect(() => setInternalValue(value), [value]);

    const isValid = (input: string) => {
        // empty string is allowed
        if (input === '') {
            return true;
        }
        // otherwise, must be digits only
        if (!input?.match(/^[0-9]+$/)) {
            return false;
        }
        // and must be in range
        if (Number(input) < 0 || Number(input) > max) {
            return false;
        }
        // and must not have multiple leading zeros
        if (input.startsWith('00')) {
            return false;
        }
        return true;
    };

    const toInteger = (input: string) => {
        if (!isValid(input)) {
            throw new Error(`cannot convert ${input} to integer`);
        }
        return (!input ? undefined : Number(input));
    };

    const toString = (integer: number | undefined) => {
        return (integer === undefined ? '' : String(integer));
    };

    const handleChange = (input: string) => {
        if (!isValid(input)) {
            const oldValue = toString(internalValue);
            inputRef.current!.value = oldValue;
            return;
        }

        const newValue = toInteger(input);
        setInternalValue(newValue);

        if (onChange) {
            onChange(newValue);
        }
    };

    return (
        <TextInput
            {...otherProps}
            inputRef={inputRef}
            inputmode="numeric"
            value={toString(internalValue)}
            onChange={handleChange}
        />
    );
};
